'use strict'

import {
  I18N_HU
} from './portal-i18n-hu.js'
import {
  PORTAL_PRESETS
} from './portal-presets.js'
import {
  KMZ_FILE_SUFFIX_POINT_NAME_TOKEN,
  KMZ_DEFAULT_ACTION_GROUP_MODE,
  KMZ_DEFAULT_ACTION_TRIGGER_TYPE,
  KMZ_ACTION_TRIGGER_TYPES,
  applyKmzActionFileSuffixDisplayMode as fnApplyKmzActionFileSuffixDisplayMode,
  normalizeKmzActionEntry as fnNormalizeKmzActionEntry,
  normalizeKmzIntegerLikeText as fnNormalizeKmzIntegerLikeText,
  normalizeKmzActionTriggerEntry as fnNormalizeKmzActionTriggerEntry,
  normalizeKmzActionGroupEntry as fnNormalizeKmzActionGroupEntry
} from './portal-kmz-action-utils.js'
import {
  getDirectElementChildren as fnGetDirectElementChildren,
  getDirectChildrenByLocalName as fnGetDirectChildrenByLocalName,
  tagMatchesLocalName as fnTagMatchesLocalName
} from './portal-xml-utils.js'
import {
  showPortalPropContextMenu
} from './portal-prop-context-bridge.js'

const _ = window._
const $ = window.jQuery
const bootstrap = window.bootstrap
const KMZ_ACTION_PRESETS = Array.isArray(PORTAL_PRESETS?.actions?.kmz) ? PORTAL_PRESETS.actions.kmz : []
const KMZ_EXPORT_TRUTH_TABLE = (PORTAL_PRESETS?.actions?.kmzExportTruthTable && typeof PORTAL_PRESETS.actions.kmzExportTruthTable === 'object')
  ? PORTAL_PRESETS.actions.kmzExportTruthTable
  : {}

const fnT = function (key, fallback) {
  const k = String(key || '').trim()
  if (!k) return String(fallback || '')
  if (Object.prototype.hasOwnProperty.call(I18N_HU, k)) return String(I18N_HU[k])
  return String(fallback || k)
}

const fnTF = function (key, vars, fallback) {
  const text = fnT(key, fallback)
  const data = (vars && typeof vars === 'object') ? vars : {}
  return text.replace(/\{([a-zA-Z0-9_]+)\}/g, function (_m, p1) {
    return Object.prototype.hasOwnProperty.call(data, p1) ? String(data[p1]) : ''
  })
}

const fnResolveI18Text = function (rawText, fallback) {
  const raw = String(rawText || '').trim()
  if (!raw) return String(fallback || '')
  const likelyKey = /^[a-z0-9_.-]+$/i.test(raw)
  if (!likelyKey) return raw
  const missingSentinel = '__PORTAL_I18N_MISSING__'
  const translated = fnT(raw, missingSentinel)
  if (translated !== missingSentinel) return translated
  return String(fallback || '')
}

const fnBuildKmzScopeTooltipText = function (scopeInfo) {
  if (!scopeInfo) return ''
  if (scopeInfo.unresolved) return fnT('kmz.scope.unresolved')
  if (scopeInfo.template && scopeInfo.waylines) return fnT('kmz.scope.both')
  if (scopeInfo.template) return fnT('kmz.scope.template_only')
  if (scopeInfo.waylines) return fnT('kmz.scope.waylines_only')
  return fnT('kmz.scope.none')
}
const fnResolveKmzScopeVariant = function (scopeInfo) {
  if (!scopeInfo) return ''
  if (scopeInfo.unresolved) return 'unresolved'
  if (scopeInfo.template && scopeInfo.waylines) return 'both'
  if (scopeInfo.template) return 'template'
  if (scopeInfo.waylines) return 'waylines'
  return 'none'
}

const fnGetXmlLocalName = function (tagName) {
  const tag = String(tagName || '').trim()
  if (!tag) return ''
  const parts = tag.split(':')
  return String(parts[parts.length - 1] || '').trim()
}

const fnGetDirectXmlChildTextByLocalName = function (parentEl, localName) {
  const child = fnGetDirectChildrenByLocalName(parentEl, localName)[0] || null
  if (!child) return ''
  return String(child.textContent || '')
}

const fnBuildObjectFromDirectXmlChildren = function (parentEl, excludedLocalNames) {
  const out = {}
  const excluded = new Set((Array.isArray(excludedLocalNames) ? excludedLocalNames : []).map((item) => String(item || '').trim()).filter((item) => !!item))
  fnGetDirectElementChildren(parentEl).forEach((childEl) => {
    const key = fnGetXmlLocalName(childEl.tagName)
    if (!key || excluded.has(key)) return
    out[key] = String(childEl.textContent || '')
  })
  return out
}

const fnParseKmzActionEntryFromXml = function (actionEl) {
  if (!actionEl || !fnTagMatchesLocalName(actionEl.tagName, 'action')) return null
  const actuator = String(fnGetDirectXmlChildTextByLocalName(actionEl, 'actionActuatorFunc') || '').trim()
  const paramsEl = fnGetDirectChildrenByLocalName(actionEl, 'actionActuatorFuncParam')[0] || null
  return fnNormalizeKmzActionEntry({
    actuator: actuator,
    params: fnBuildObjectFromDirectXmlChildren(paramsEl)
  })
}

const fnParseKmzActionGroupPresetXml = function (rawXml, defaultPointIndex) {
  const xml = String(rawXml || '').trim()
  if (!xml || typeof DOMParser === 'undefined') return []
  try {
    const parser = new DOMParser()
    const wrappedXml = '<root xmlns:wpml="http://www.dji.com/wpmz/1.0.6">' + xml + '</root>'
    const xmlDoc = parser.parseFromString(wrappedXml, 'application/xml')
    if (!xmlDoc || !xmlDoc.documentElement) return []
    if (xmlDoc.getElementsByTagName('parsererror').length) return []
    return fnGetDirectChildrenByLocalName(xmlDoc.documentElement, 'actionGroup')
      .map((groupEl, groupIndex) => {
        const actionTriggerEl = fnGetDirectChildrenByLocalName(groupEl, 'actionTrigger')[0] || null
        const actions = fnGetDirectChildrenByLocalName(groupEl, 'action')
          .map((actionEl) => fnParseKmzActionEntryFromXml(actionEl))
          .filter((item) => !!item)
        return fnNormalizeKmzActionGroupEntry({
          actionGroupId: groupIndex,
          actionGroupStartIndex: defaultPointIndex,
          actionGroupEndIndex: defaultPointIndex,
          actionGroupMode: String(fnGetDirectXmlChildTextByLocalName(groupEl, 'actionGroupMode') || '').trim() || KMZ_DEFAULT_ACTION_GROUP_MODE,
          actionTrigger: fnNormalizeKmzActionTriggerEntry(fnBuildObjectFromDirectXmlChildren(actionTriggerEl)),
          actions: actions
        }, defaultPointIndex)
      })
      .filter((item) => !!item)
  } catch (_error) {
    return []
  }
}

const fnBuildKmzActionGroupPresetLabel = function (groups, fallbackIndex) {
  const parts = []
  ;(Array.isArray(groups) ? groups : []).forEach((group, groupIndex) => {
    const safeGroupIndex = Number.isFinite(Number(groupIndex)) ? Number(groupIndex) : Number(fallbackIndex) || 0
    parts.push('g' + String(safeGroupIndex))
    ;(Array.isArray(group && group.actions) ? group.actions : []).forEach((action) => {
      const actuator = String(action && action.actuator || '').trim()
      if (actuator) parts.push(actuator)
    })
  })
  return parts.join(' ').trim()
}

const fnGetKmzActionGroupPresetOptions = function (defaultPointIndex) {
  if (typeof document === 'undefined') return []
  return Array.from(document.querySelectorAll('[data-tpl-action-group-preset]'))
    .map((node, nodeIndex) => {
      const presetId = String(node.getAttribute('data-action-preset-id') || node.id || ('preset-' + String(nodeIndex))).trim()
      if (!presetId) return null
      const groups = fnParseKmzActionGroupPresetXml(node.textContent, defaultPointIndex)
      if (!groups.length) return null
      return {
        value: presetId,
        label: fnBuildKmzActionGroupPresetLabel(groups, nodeIndex),
        groups: groups
      }
    })
    .filter((item) => !!item)
}

const fnResolvePropValueHelp = function (_path, pathNoNs, value, fallbackTooltip) {
  if (typeof window !== 'undefined' && typeof window.PORTAL_RESOLVE_PROP_VALUE_HELP === 'function') {
    return window.PORTAL_RESOLVE_PROP_VALUE_HELP(_path, pathNoNs, value, fallbackTooltip)
  }
  const val = String(value || '').trim()
  const fallbackPath = String(pathNoNs || _path || '').trim()
  const tipRaw = String(fallbackTooltip || '').trim()
  const tip = fnResolveI18Text(tipRaw, '')
  if (tip) return tip
  if (tipRaw && !/^[a-z0-9_.-]+$/i.test(tipRaw)) return tipRaw
  return fnTF('dji.value_set', { field: fallbackPath, value: val }, fallbackPath + ': ' + val)
}

const fnShowPropContextMenu = function (event, _data) {
  showPortalPropContextMenu(event, _data)
}

const fnGetKmzActionPreset = function (actuator) {
  const key = String(actuator || '').trim()
  if (!key) return null
  return KMZ_ACTION_PRESETS.find((item) => String(item && item.value || '').trim() === key) || null
}

const fnGetKmzAllowedParamKeys = function (actuator) {
  const preset = fnGetKmzActionPreset(actuator)
  if (!preset || !preset.defaultParams || typeof preset.defaultParams !== 'object') return []
  return Object.keys(preset.defaultParams)
    .map((k) => String(k || '').trim())
    .filter((k) => !!k)
}

const fnGetKmzParamMeta = function (actuator, paramKey) {
  const preset = fnGetKmzActionPreset(actuator)
  if (!preset || !preset.paramMeta || typeof preset.paramMeta !== 'object') return null
  const key = String(paramKey || '').trim()
  if (!key) return null
  const meta = preset.paramMeta[key]
  return (meta && typeof meta === 'object') ? meta : null
}

const fnToScopeFlag = function (rawValue, fallbackValue) {
  if (rawValue === undefined || rawValue === null || rawValue === '') return !!fallbackValue
  if (typeof rawValue === 'boolean') return rawValue
  const text = String(rawValue).trim().toLowerCase()
  if (!text) return !!fallbackValue
  if (text === '1' || text === 'true' || text === 'yes' || text === 'on') return true
  if (text === '0' || text === 'false' || text === 'no' || text === 'off') return false
  const n = Number(text)
  if (Number.isFinite(n)) return n !== 0
  return !!fallbackValue
}

const fnResolveActionParamExportScope = function (actuator, paramKey) {
  const table = (KMZ_EXPORT_TRUTH_TABLE && typeof KMZ_EXPORT_TRUTH_TABLE === 'object') ? KMZ_EXPORT_TRUTH_TABLE : {}
  const actionName = String(actuator || '').trim()
  const key = String(paramKey || '').trim()
  const unknownActionScope = (table.unknownActionScope && typeof table.unknownActionScope === 'object')
    ? table.unknownActionScope
    : { template: 0, waylines: 0 }
  const unknownParamScope = (table.unknownParamScope && typeof table.unknownParamScope === 'object')
    ? table.unknownParamScope
    : { template: 0, waylines: 0 }

  const actionScopeRule = (table.actionScopes && typeof table.actionScopes === 'object')
    ? table.actionScopes[actionName]
    : null
  const actionScopeKnown = !!(actionScopeRule && typeof actionScopeRule === 'object')
  const actionTemplateAllowed = fnToScopeFlag(actionScopeRule && actionScopeRule.template, fnToScopeFlag(unknownActionScope.template, false))
  const actionWaylinesAllowed = fnToScopeFlag(actionScopeRule && actionScopeRule.waylines, fnToScopeFlag(unknownActionScope.waylines, false))

  const paramRuleMap = (table.paramScopes && typeof table.paramScopes === 'object')
    ? table.paramScopes[actionName]
    : null
  const paramScopeRule = (paramRuleMap && typeof paramRuleMap === 'object')
    ? paramRuleMap[key]
    : null
  const paramScopeKnown = !!(paramScopeRule && typeof paramScopeRule === 'object')
  const paramTemplateAllowed = fnToScopeFlag(paramScopeRule && paramScopeRule.template, fnToScopeFlag(unknownParamScope.template, false))
  const paramWaylinesAllowed = fnToScopeFlag(paramScopeRule && paramScopeRule.waylines, fnToScopeFlag(unknownParamScope.waylines, false))
  const unresolved = (!actionScopeKnown || !paramScopeKnown) ? 1 : 0
  if (unresolved) {
    return {
      template: 1,
      waylines: 1,
      unresolved: 1
    }
  }

  return {
    template: actionTemplateAllowed && paramTemplateAllowed ? 1 : 0,
    waylines: actionWaylinesAllowed && paramWaylinesAllowed ? 1 : 0,
    unresolved: 0
  }
}

const fnApplyParamScopeColorToLine = function ($line, actuator, paramKey) {
  if (!$line || typeof $line.attr !== 'function') return
  const scope = fnResolveActionParamExportScope(actuator, paramKey)
  $line.attr('data-kmz-param-scope-template', String(scope.template))
  $line.attr('data-kmz-param-scope-waylines', String(scope.waylines))
  $line.attr('data-kmz-param-scope-unresolved', String(scope.unresolved))
  $line.attr('title', 'template=' + String(scope.template) + ', waylines=' + String(scope.waylines) + (scope.unresolved ? ', unresolved=1' : ''))
}

const fnValidateKmzParamValue = function (actuator, paramKey, rawValue) {
  const meta = fnGetKmzParamMeta(actuator, paramKey)
  if (!meta) return { ok: true, message: '' }
  const valueText = String(rawValue === null || rawValue === undefined ? '' : rawValue).trim()
  if (!valueText) return { ok: true, message: '' }

  if (Array.isArray(meta.enum) && meta.enum.length) {
    const allowed = meta.enum.map((v) => String(v))
    if (!allowed.includes(valueText)) {
      return {
        ok: false,
        message: fnTF('validate.kmz_allowed_values', { values: allowed.join(', ') })
      }
    }
    return { ok: true, message: '' }
  }

  if (String(meta.type || '').toLowerCase() === 'number' || meta.min !== undefined || meta.max !== undefined) {
    const n = Number(valueText)
    if (!Number.isFinite(n)) return { ok: false, message: fnT('validate.kmz_number_required') }
    if (meta.min !== undefined && n < Number(meta.min)) return { ok: false, message: fnTF('validate.kmz_minimum', { min: String(meta.min) }) }
    if (meta.max !== undefined && n > Number(meta.max)) return { ok: false, message: fnTF('validate.kmz_maximum', { max: String(meta.max) }) }
  }
  return { ok: true, message: '' }
}

const fnBuildKmzParamInfoText = function (actuator, paramKey, scopeInfo) {
  const meta = fnGetKmzParamMeta(actuator, paramKey)
  const key = String(paramKey || '').trim()
  const lines = []
  lines.push(fnT('dji.parameter_prefix') + ' ' + key)
  if (!meta) {
    lines.push(fnT('dji.no_description'))
  } else if (meta.description) {
    const rawDescription = String(meta.description).trim()
    const resolvedDescription = fnResolveI18Text(rawDescription, rawDescription)
    lines.push(fnT('dji.description_prefix') + ' ' + resolvedDescription)
  }
  if (meta && Array.isArray(meta.enum) && meta.enum.length) {
    lines.push(fnT('dji.values_prefix') + ' ' + meta.enum.map((v) => String(v)).join(', '))
  }
  const hasMin = !!(meta && meta.min !== undefined)
  const hasMax = !!(meta && meta.max !== undefined)
  if (hasMin || hasMax) {
    lines.push(fnT('dji.range_prefix') + ' ' + (hasMin ? String(meta.min) : '-inf') + ' .. ' + (hasMax ? String(meta.max) : '+inf'))
  }
  if (scopeInfo) {
    lines.push(fnBuildKmzScopeTooltipText(scopeInfo))
  }
  return lines.join('\n')
}

const fnBuildKmzParamShortHelp = function (actuator, paramKey) {
  const meta = fnGetKmzParamMeta(actuator, paramKey)
  const key = String(paramKey || '').trim()
  if (meta && meta.description) {
    const rawDescription = String(meta.description || '').trim()
    const resolvedDescription = fnResolveI18Text(rawDescription, rawDescription)
    if (resolvedDescription) return resolvedDescription
  }
  const translatedLabel = fnT('kmz_action.param.' + key, '')
  if (translatedLabel && translatedLabel !== ('kmz_action.param.' + key)) return translatedLabel
  return key
}

const fnGetKmzParamEnumValues = function (actuator, paramKey) {
  const meta = fnGetKmzParamMeta(actuator, paramKey)
  if (!meta || !Array.isArray(meta.enum) || !meta.enum.length) return []
  return meta.enum
    .map((item) => String(item === undefined || item === null ? '' : item).trim())
    .filter((item) => !!item)
}

const fnNormalizeKmzActionParamsForActuator = function (actuator, params) {
  const src = (params && typeof params === 'object') ? params : {}
  const allowed = fnGetKmzAllowedParamKeys(actuator)
  const out = {}
  if (!allowed.length) {
    Object.keys(src).forEach((key) => {
      const k = String(key || '').trim()
      if (!k) return
      const raw = src[key]
      if (raw === undefined || raw === null) return
      out[k] = String(raw)
    })
    return out
  }
  allowed.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(src, key)) return
    const raw = src[key]
    if (raw === undefined || raw === null) return
    out[key] = String(raw)
  })
  if (allowed.includes('useGlobalPayloadLensIndex')) {
    const hasPayloadLensIndex = String(out.payloadLensIndex || '').trim() !== ''
    if (!hasPayloadLensIndex) {
      out.useGlobalPayloadLensIndex = '1'
    } else if (!Object.prototype.hasOwnProperty.call(out, 'useGlobalPayloadLensIndex')) {
      out.useGlobalPayloadLensIndex = '0'
    }
  }
  return out
}

const fnNormalizeKmzActionGroupsForPoint = function (groups, pointIndex, options) {
  const idx = Number.isFinite(Number(pointIndex)) ? Number(pointIndex) : 0
  const opts = _.assign({
    actionGroupIdAllocator: null,
    pruneEmptyGroups: false,
    alignReachPointRangeToIndex: false,
    alignBetweenAdjacentPointsRangeToIndex: false,
    ensureMultipleTimingTriggerParam: false
  }, options || {})
  const normalizedGroups = (Array.isArray(groups) ? groups : [])
    .map((group) => fnNormalizeKmzActionGroupEntry(group, idx))
    .filter((item) => !!item)
  let out = normalizedGroups.map((group) => {
    const next = _.cloneDeep(group)
    if (typeof opts.actionGroupIdAllocator === 'function') {
      next.actionGroupId = fnNormalizeKmzIntegerLikeText(opts.actionGroupIdAllocator(), idx)
    } else {
      next.actionGroupId = fnNormalizeKmzIntegerLikeText(next.actionGroupId, idx)
    }
    next.actionGroupStartIndex = fnNormalizeKmzIntegerLikeText(next.actionGroupStartIndex, idx)
    next.actionGroupEndIndex = fnNormalizeKmzIntegerLikeText(next.actionGroupEndIndex, idx)
    next.actionGroupMode = String(next.actionGroupMode || KMZ_DEFAULT_ACTION_GROUP_MODE).trim() || KMZ_DEFAULT_ACTION_GROUP_MODE
    next.actionTrigger = fnNormalizeKmzActionTriggerEntry(next.actionTrigger)
    const triggerType = String(_.get(next, 'actionTrigger.actionTriggerType') || '').trim().toLowerCase()
    if (opts.alignReachPointRangeToIndex && triggerType === 'reachpoint') {
      next.actionGroupStartIndex = String(idx)
      next.actionGroupEndIndex = String(idx)
    } else if (opts.alignBetweenAdjacentPointsRangeToIndex && triggerType === 'betweenadjacentpoints') {
      next.actionGroupStartIndex = String(idx)
      next.actionGroupEndIndex = String(idx + 1)
    }
    if (opts.ensureMultipleTimingTriggerParam && triggerType === 'multipletiming') {
      const triggerParam = String(_.get(next, 'actionTrigger.actionTriggerParam') || '').trim()
      if (!triggerParam) _.set(next, 'actionTrigger.actionTriggerParam', '1')
    }
    next.actions = (Array.isArray(next.actions) ? next.actions : [])
      .map(fnNormalizeKmzActionEntry)
      .map((item) => item ? _.assign({}, item, { params: fnNormalizeKmzActionParamsForActuator(item.actuator, item.params) }) : item)
      .filter((item) => !!item)
    return next
  })
  if (opts.pruneEmptyGroups) out = out.filter((group) => Array.isArray(group.actions) && group.actions.length > 0)
  return out
}

let KMZ_POINT_EDITOR_TEMPLATE_HTML_CACHE = null

const fnGetKmzPointEditorTemplateHtml = function () {
  if (KMZ_POINT_EDITOR_TEMPLATE_HTML_CACHE !== null) return KMZ_POINT_EDITOR_TEMPLATE_HTML_CACHE
  if (typeof document === 'undefined') {
    KMZ_POINT_EDITOR_TEMPLATE_HTML_CACHE = ''
    return KMZ_POINT_EDITOR_TEMPLATE_HTML_CACHE
  }
  const tplNode = document.querySelector('[data-tpl-kmz-point-editor]')
  KMZ_POINT_EDITOR_TEMPLATE_HTML_CACHE = tplNode ? String(tplNode.innerHTML || '').trim() : ''
  return KMZ_POINT_EDITOR_TEMPLATE_HTML_CACHE
}

const fnBuildKmzPointEditorTemplateBank = function () {
  const html = fnGetKmzPointEditorTemplateHtml()
  if (!html) return $()
  const $parsed = $(html)
  const $bank = $parsed.filter('[data-kmz-editor-templatebank]').first()
  if ($bank.length) return $bank
  return $parsed.find('[data-kmz-editor-templatebank]').first()
}

const fnCloneKmzPointEditorNode = function ($bank, selector) {
  if (!$bank || typeof $bank.find !== 'function') return $()
  if ($bank.is(selector)) return $bank.clone()
  const $node = $bank.find(selector).first()
  return $node.length ? $node.clone() : $()
}

const fnFindKmzEditorNode = function ($root, selector) {
  if (!$root || typeof $root.find !== 'function') return $()
  return $root.find(selector).first()
}

const fnParseKmzEditorIndexAttr = function (rawValue) {
  const n = Number(rawValue)
  return Number.isInteger(n) && n >= 0 ? n : -1
}

const fnGetClosestKmzEditorIndex = function (node, selector, attrName) {
  const $host = $(node).closest(selector)
  if (!$host.length) return -1
  return fnParseKmzEditorIndexAttr($host.attr(attrName))
}

const fnDisposeBootstrapTooltip = function ($node) {
  if (!$node || typeof $node.get !== 'function' || !$node.length) return
  if (!bootstrap || !bootstrap.Tooltip) return
  const instance = bootstrap.Tooltip.getInstance($node.get(0))
  if (instance) instance.dispose()
}

const renderKmzActionsEditor = function ($container, initialActions, onChange, options) {
  const opts = _.assign({
    fileSuffixMode: 'raw',
    pointNameResolver: null
  }, options || {})
  const $bank = fnBuildKmzPointEditorTemplateBank()
  const $root = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-actionseditor]')
  if (!$root.length) {
    $container.empty()
    return
  }
  const fnResolvePointName = function () {
    if (typeof opts.pointNameResolver === 'function') return String(opts.pointNameResolver() || '')
    return String(opts.pointNameResolver || '')
  }
  const fnGetFileSuffixDisplayValue = function (currentValue) {
    const mode = String(opts.fileSuffixMode || 'raw').trim().toLowerCase()
    if (mode === 'token') return KMZ_FILE_SUFFIX_POINT_NAME_TOKEN
    if (mode === 'pointname') return fnResolvePointName()
    return String(currentValue === undefined || currentValue === null ? '' : currentValue)
  }
  const fnApplyDisplayModeToParams = function (params) {
    const src = (params && typeof params === 'object') ? params : {}
    if (!Object.prototype.hasOwnProperty.call(src, 'fileSuffix')) return src
    const current = String(src.fileSuffix === undefined ? '' : src.fileSuffix)
    const next = fnGetFileSuffixDisplayValue(current)
    if (next === current) return src
    return _.assign({}, src, { fileSuffix: next })
  }
  const fnApplyDisplayModeToActions = function (actions) {
    return fnApplyKmzActionFileSuffixDisplayMode(actions, opts.fileSuffixMode, fnResolvePointName)
  }
  const state = {
    actions: fnApplyDisplayModeToActions(initialActions)
  }
  const $title = fnFindKmzEditorNode($root, '[data-kmz-editor-actionseditor-title]')
  const $addLabel = fnFindKmzEditorNode($root, '[data-kmz-editor-actionadd-label]')
  const $empty = fnFindKmzEditorNode($root, '[data-kmz-editor-actionseditor-empty]')
  const $list = fnFindKmzEditorNode($root, '[data-kmz-editor-actionlist]')

  const fnGetActionAtIndex = function (actionIndex) {
    const idx = Number.isFinite(Number(actionIndex)) ? Number(actionIndex) : -1
    if (idx < 0 || idx >= state.actions.length) return null
    return state.actions[idx] || null
  }
  const fnSyncActionPresetBadges = function ($row) {
    const current = String(fnFindKmzEditorNode($row, '[data-kmz-editor-actionactuator-input]').val() || '').trim()
    $row.find('[data-kmz-editor-actionbadge]').each(function () {
      const $badge = $(this)
      const isActive = String($badge.attr('data-kmz-editor-actuator') || '').trim() === current
      $badge.toggleClass('bg-primary', isActive)
      $badge.toggleClass('text-white', isActive)
      $badge.toggleClass('bg-light', !isActive)
      $badge.toggleClass('text-dark', !isActive)
    })
  }
  const fnSyncActionParamPresetBadges = function ($line) {
    const current = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamvalue]').val() || '').trim()
    $line.find('[data-kmz-editor-actionparampreset]').each(function () {
      const $badge = $(this)
      const isActive = String($badge.attr('data-kmz-editor-actionparampresetvalue') || '').trim() === current
      $badge.toggleClass('bg-primary', isActive)
      $badge.toggleClass('text-white', isActive)
      $badge.toggleClass('bg-light', !isActive)
      $badge.toggleClass('text-dark', !isActive)
    })
  }
  const fnSyncActionParamPresetUi = function ($line, actuator) {
    const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
    const enumValues = fnGetKmzParamEnumValues(actuator, liveKey)
    const $value = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamvalue]')
    const $presetWrap = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparampresetwrap]')
    const $presetHost = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparampresethost]')
    const $editBtn = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparampresetedit]')
    if (!$value.length || !$presetWrap.length || !$presetHost.length) return
    $presetHost.empty()
    if (!enumValues.length) {
      $presetWrap.addClass('d-none')
      $value.removeClass('d-none')
      return
    }
    enumValues.forEach((enumValue) => {
      const $badge = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-actionparampreset]')
      if (!$badge.length) return
      $badge
        .attr('data-kmz-editor-actionparampresetvalue', enumValue)
        .attr('title', fnTF('action.set_value_to', { value: enumValue }))
        .attr('data-bs-toggle', 'tooltip')
        .attr('data-bs-placement', 'top')
        .text(enumValue)
      $presetHost.append($badge)
    })
    $editBtn
      .attr('title', fnT('action.custom_value_edit'))
      .attr('data-bs-toggle', 'tooltip')
      .attr('data-bs-placement', 'top')
    const currentValue = String($value.val() || '').trim()
    const canHideRawInput = currentValue === '' || enumValues.includes(currentValue)
    $value.toggleClass('d-none', canHideRawInput)
    $presetWrap.removeClass('d-none')
    fnSyncActionParamPresetBadges($line)
  }
    const fnSyncParamInfoTooltip = function ($line, actuator) {
      const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
      const scopeInfo = fnResolveActionParamExportScope(actuator, liveKey)
      const infoText = fnBuildKmzParamInfoText(actuator, liveKey, scopeInfo)
      const htmlText = String(infoText || '').replace(/\\n|\n/g, '<br>')
      const $info = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparaminfo]')
      $info
      .removeAttr('title')
      .attr('data-bs-title', htmlText)
      .attr('data-bs-toggle', 'tooltip')
      .attr('data-bs-placement', 'top')
      .attr('data-bs-html', 'true')
      fnDisposeBootstrapTooltip($info)
    }
    const fnBuildKmzActionParamContextHelp = function (actuator, paramKey) {
      const scopeInfo = fnResolveActionParamExportScope(actuator, paramKey)
      const shortHelp = fnBuildKmzParamShortHelp(actuator, paramKey)
      const baseDetailedHelp = fnBuildKmzParamInfoText(actuator, paramKey, scopeInfo)
      return {
        scopeInfo: scopeInfo,
        shortHelp: shortHelp,
        detailedHelp: baseDetailedHelp,
        kmzScopeVariant: fnResolveKmzScopeVariant(scopeInfo)
      }
    }
  const fnApplyParamValidation = function ($line, actuator) {
    const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
    const $value = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamvalue]')
    const verdict = fnValidateKmzParamValue(actuator, liveKey, $value.val())
    $value.toggleClass('is-invalid', !verdict.ok)
    if (!verdict.ok) {
      $value.attr('title', verdict.message)
    } else {
      $value.removeAttr('title')
    }
    return verdict.ok
  }
  const fnSyncActionParamRows = function ($row, actuator) {
    $row.find('[data-kmz-editor-actionparamrow]').each(function () {
      const $line = $(this)
      const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
      fnSyncActionParamPresetUi($line, actuator)
      fnApplyParamScopeColorToLine($line, actuator, liveKey)
      fnSyncParamInfoTooltip($line, actuator)
      fnApplyParamValidation($line, actuator)
    })
  }
  const fnEmit = function () {
    const normalized = (Array.isArray(state.actions) ? state.actions : [])
      .map(fnNormalizeKmzActionEntry)
      .map((item) => item ? _.assign({}, item, { params: fnNormalizeKmzActionParamsForActuator(item.actuator, item.params) }) : item)
      .filter((item) => !!item)
    if (typeof onChange === 'function') onChange(normalized.slice())
  }
  const fnRender = function () {
    $title.text(fnT('action.kmz.title'))
    $addLabel.text(fnT('action.add'))
    $list.empty()
    if (!state.actions.length) {
      $empty.text(fnT('action.none')).removeClass('d-none')
      return
    }
    $empty.addClass('d-none').text('')
    state.actions.forEach((action, idx) => {
      const $row = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-actionrow]')
      $row.attr('data-kmz-editor-actionindex', String(idx))
      fnFindKmzEditorNode($row, '[data-kmz-editor-actionrow-title]').text(fnTF('action.item', { n: idx + 1 }))
      fnFindKmzEditorNode($row, '[data-kmz-editor-actiontype-label]').text(fnT('action.type'))
      fnFindKmzEditorNode($row, '[data-kmz-editor-actionparams-label]').text(fnT('action.params'))
      fnFindKmzEditorNode($row, '[data-kmz-editor-actionparamadd-label]').text('+ ' + fnT('action.param'))
      fnFindKmzEditorNode($row, '[data-kmz-editor-actionparams-empty]').text(fnT('action.no_param'))
      fnFindKmzEditorNode($row, '[data-kmz-editor-actiondelete]').text(fnT('action.delete'))
      const $moveUp = fnFindKmzEditorNode($row, '[data-kmz-editor-actionmoveup]')
      const $moveDown = fnFindKmzEditorNode($row, '[data-kmz-editor-actionmovedown]')
      $moveUp.prop('disabled', idx === 0).toggleClass('opacity-50', idx === 0)
      $moveDown.prop('disabled', idx === state.actions.length - 1).toggleClass('opacity-50', idx === state.actions.length - 1)
      const $actuatorInput = fnFindKmzEditorNode($row, '[data-kmz-editor-actionactuator-input]')
      $actuatorInput.val(String(action.actuator || ''))
      fnFindKmzEditorNode($row, '[data-kmz-editor-actionactuatoredit]')
        .attr('title', fnT('action.edit_custom'))
        .attr('data-bs-toggle', 'tooltip')
        .attr('data-bs-placement', 'top')
      const $badgeHost = fnFindKmzEditorNode($row, '[data-kmz-editor-actionbadgehost]')
      KMZ_ACTION_PRESETS.forEach((preset) => {
        const value = String((preset && preset.value) || '').trim()
        if (!value) return
        const tooltip = fnResolveI18Text(String((preset && preset.tooltip) || '').trim(), '') || fnTF('action.type_set_to', { value: value })
        const $badge = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-actionbadge]')
        $badge
          .attr('data-kmz-editor-actuator', value)
          .attr('data-kmz-editor-tooltip', tooltip)
          .attr('data-bs-toggle', 'tooltip')
          .attr('data-bs-placement', 'top')
          .attr('title', tooltip)
          .text(value)
        $badgeHost.append($badge)
      })
      fnSyncActionPresetBadges($row)
      action.params = fnNormalizeKmzActionParamsForActuator(action.actuator, action.params)
      const paramEntries = Object.entries(action.params || {})
      const $paramList = fnFindKmzEditorNode($row, '[data-kmz-editor-actionparamlist]')
      const $paramsEmpty = fnFindKmzEditorNode($row, '[data-kmz-editor-actionparams-empty]')
      $paramsEmpty.toggleClass('d-none', !!paramEntries.length)
      paramEntries.forEach(([paramKey, paramValue], paramIndex) => {
        const $line = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-actionparamrow]')
        $line
          .attr('data-kmz-editor-actionindex', String(idx))
          .attr('data-kmz-editor-paramindex', String(paramIndex))
        fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val(String(paramKey || ''))
        fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamvalue]').val(String(paramValue || ''))
        fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamdelete]').text(fnT('action.delete'))
        fnSyncActionParamPresetUi($line, action.actuator)
        fnApplyParamScopeColorToLine($line, action.actuator, paramKey)
        fnSyncParamInfoTooltip($line, action.actuator)
        fnApplyParamValidation($line, action.actuator)
        $paramList.append($line)
      })
      $list.append($row)
    })
  }

  $root
    .off('.portalKmzActions')
    .on('click.portalKmzActions', '[data-kmz-editor-actionadd]', function () {
      const preset = KMZ_ACTION_PRESETS[0] || { value: 'hover', defaultParams: { hoverTime: '1' } }
      state.actions.push({
        actuator: String(preset.value || 'hover'),
        params: _.cloneDeep(preset.defaultParams || {})
      })
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionactuatoredit]', function () {
      const $row = $(this).closest('[data-kmz-editor-actionrow]')
      const $input = fnFindKmzEditorNode($row, '[data-kmz-editor-actionactuator-input]')
      const hidden = $input.hasClass('d-none')
      $input.toggleClass('d-none', !hidden)
      if (hidden) $input.trigger('focus')
    })
    .on('contextmenu.portalKmzActions', '[data-kmz-editor-actionbadge]', function (event) {
      const value = String($(this).attr('data-kmz-editor-actuator') || '').trim()
      if (!value) return
      const tooltip = String($(this).attr('data-kmz-editor-tooltip') || $(this).attr('title') || '').trim()
      const detailedHelp = fnResolvePropValueHelp('wpml:actionGroup.wpml:action.wpml:actionActuatorFunc', 'wpml:actionGroup.wpml:action.wpml:actionActuatorFunc', value, tooltip)
      fnShowPropContextMenu(event, {
        label: fnT('action.type'),
        fullName: 'wpml:actionGroup.wpml:action.wpml:actionActuatorFunc',
        helpShort: tooltip,
        helpDetailed: detailedHelp
      })
    })
    .on('contextmenu.portalKmzActions', '[data-kmz-editor-actionparaminfo], [data-kmz-editor-actionparampreset]', function (event) {
      const $line = $(this).closest('[data-kmz-editor-actionparamrow]')
      const actionIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-actionindex'))
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
      if (!liveKey) return
      const contextHelp = fnBuildKmzActionParamContextHelp(action.actuator, liveKey)
      fnShowPropContextMenu(event, {
        label: liveKey,
        fullName: 'wpml:actionGroup.wpml:action.wpml:actionActuatorFuncParam.' + liveKey,
        helpShort: contextHelp.shortHelp,
        helpDetailed: contextHelp.detailedHelp,
        kmzScopeVariant: contextHelp.kmzScopeVariant
      })
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionbadge]', function () {
      const actionIndex = fnGetClosestKmzEditorIndex(this, '[data-kmz-editor-actionrow]', 'data-kmz-editor-actionindex')
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      const nextActuator = String($(this).attr('data-kmz-editor-actuator') || '').trim()
      if (!nextActuator) return
      action.actuator = nextActuator
      const preset = fnGetKmzActionPreset(nextActuator)
      action.params = fnApplyDisplayModeToParams(_.cloneDeep((preset && preset.defaultParams) || {}))
      fnRender()
      fnEmit()
    })
    .on('input.portalKmzActions', '[data-kmz-editor-actionactuator-input]', function () {
      const $row = $(this).closest('[data-kmz-editor-actionrow]')
      const actionIndex = fnParseKmzEditorIndexAttr($row.attr('data-kmz-editor-actionindex'))
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      action.actuator = String($(this).val() || '').trim()
      action.params = fnNormalizeKmzActionParamsForActuator(action.actuator, action.params)
      fnSyncActionPresetBadges($row)
      fnSyncActionParamRows($row, action.actuator)
      fnEmit()
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionparamadd]', function () {
      const actionIndex = fnGetClosestKmzEditorIndex(this, '[data-kmz-editor-actionrow]', 'data-kmz-editor-actionindex')
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      action.params = action.params || {}
      const allowed = fnGetKmzAllowedParamKeys(action.actuator)
      if (allowed.length) {
        const nextKey = allowed.find((key) => !Object.prototype.hasOwnProperty.call(action.params, key))
        if (!nextKey) return
        action.params[nextKey] = nextKey === 'fileSuffix'
          ? fnGetFileSuffixDisplayValue('')
          : ''
      } else {
        let i = 1
        let nextKey = 'param' + i
        while (Object.prototype.hasOwnProperty.call(action.params, nextKey)) {
          i += 1
          nextKey = 'param' + i
        }
        action.params[nextKey] = ''
      }
      fnRender()
      fnEmit()
    })
    .on('input.portalKmzActions', '[data-kmz-editor-actionparamkey]', function () {
      const $line = $(this).closest('[data-kmz-editor-actionparamrow]')
      const actionIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-actionindex'))
      const paramIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-paramindex'))
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      const entries = Object.entries(action.params || {})
      const previousKey = entries[paramIndex] ? String(entries[paramIndex][0] || '').trim() : ''
      const previousValue = previousKey ? action.params[previousKey] : ''
      const nextKey = String($(this).val() || '').trim()
      if (previousKey) delete action.params[previousKey]
      if (nextKey) action.params[nextKey] = previousValue
      action.params = fnNormalizeKmzActionParamsForActuator(action.actuator, action.params)
      fnRender()
      fnEmit()
    })
    .on('input.portalKmzActions', '[data-kmz-editor-actionparamvalue]', function () {
      const $line = $(this).closest('[data-kmz-editor-actionparamrow]')
      const actionIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-actionindex'))
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
      if (!liveKey) return
      fnSyncActionParamPresetBadges($line)
      const ok = fnApplyParamValidation($line, action.actuator)
      if (!ok) return
      action.params[liveKey] = String($(this).val() || '')
      fnEmit()
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionparampresetedit]', function () {
      const $line = $(this).closest('[data-kmz-editor-actionparamrow]')
      const $value = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamvalue]')
      if (!$value.length) return
      const hidden = $value.hasClass('d-none')
      $value.toggleClass('d-none', !hidden)
      if (hidden) $value.trigger('focus')
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionparampreset]', function () {
      const $line = $(this).closest('[data-kmz-editor-actionparamrow]')
      const actionIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-actionindex'))
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
      if (!liveKey) return
      const nextValue = String($(this).attr('data-kmz-editor-actionparampresetvalue') || '').trim()
      const $value = fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamvalue]')
      $value.val(nextValue)
      fnSyncActionParamPresetBadges($line)
      const ok = fnApplyParamValidation($line, action.actuator)
      if (!ok) return
      action.params[liveKey] = nextValue
      fnEmit()
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionparamdelete]', function () {
      const $line = $(this).closest('[data-kmz-editor-actionparamrow]')
      const actionIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-actionindex'))
      const action = fnGetActionAtIndex(actionIndex)
      if (!action) return
      const liveKey = String(fnFindKmzEditorNode($line, '[data-kmz-editor-actionparamkey]').val() || '').trim()
      if (liveKey) delete action.params[liveKey]
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionmoveup]', function () {
      const actionIndex = fnGetClosestKmzEditorIndex(this, '[data-kmz-editor-actionrow]', 'data-kmz-editor-actionindex')
      if (actionIndex <= 0) return
      const temp = state.actions[actionIndex - 1]
      state.actions[actionIndex - 1] = state.actions[actionIndex]
      state.actions[actionIndex] = temp
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actionmovedown]', function () {
      const actionIndex = fnGetClosestKmzEditorIndex(this, '[data-kmz-editor-actionrow]', 'data-kmz-editor-actionindex')
      if (actionIndex < 0 || actionIndex >= state.actions.length - 1) return
      const temp = state.actions[actionIndex + 1]
      state.actions[actionIndex + 1] = state.actions[actionIndex]
      state.actions[actionIndex] = temp
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzActions', '[data-kmz-editor-actiondelete]', function () {
      const actionIndex = fnGetClosestKmzEditorIndex(this, '[data-kmz-editor-actionrow]', 'data-kmz-editor-actionindex')
      if (actionIndex < 0) return
      state.actions.splice(actionIndex, 1)
      fnRender()
      fnEmit()
    })

  $container.empty().append($root)
  fnRender()
}

const renderKmzActionGroupsEditor = function ($container, initialGroups, onChange, options) {
  const opts = _.assign({
    fileSuffixMode: 'raw',
    pointNameResolver: null,
    defaultPointIndex: 0,
    emitInitial: true
  }, options || {})
  const defaultPointIndex = Number.isFinite(Number(opts.defaultPointIndex)) ? Number(opts.defaultPointIndex) : 0
  const $bank = fnBuildKmzPointEditorTemplateBank()
  const $root = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-groupeditor]')
  if (!$root.length) {
    $container.empty()
    return
  }
  const fnBuildDefaultGroup = function (groupIndexFallback) {
    const fallback = Number.isFinite(Number(groupIndexFallback)) ? Number(groupIndexFallback) : defaultPointIndex
    return fnNormalizeKmzActionGroupEntry({
      actionGroupId: fallback,
      actionGroupStartIndex: defaultPointIndex,
      actionGroupEndIndex: defaultPointIndex,
      actionGroupMode: KMZ_DEFAULT_ACTION_GROUP_MODE,
      actionTrigger: { actionTriggerType: KMZ_DEFAULT_ACTION_TRIGGER_TYPE },
      actions: []
    }, defaultPointIndex)
  }
  const initial = fnNormalizeKmzActionGroupsForPoint(initialGroups, defaultPointIndex)
  const state = {
    groups: initial.length ? initial : [],
    activeIndex: 0
  }
  const $title = fnFindKmzEditorNode($root, '[data-kmz-editor-groupeditor-title]')
  const $addLabel = fnFindKmzEditorNode($root, '[data-kmz-editor-groupadd-label]')
  const $empty = fnFindKmzEditorNode($root, '[data-kmz-editor-groupeditor-empty]')
  const $nav = fnFindKmzEditorNode($root, '[data-kmz-editor-groupnav]')
  const $cardHost = fnFindKmzEditorNode($root, '[data-kmz-editor-groupcardhost]')
  const $presetSelect = fnFindKmzEditorNode($root, '[data-kmz-editor-presetselect]')

  const fnEnsureActiveIndex = function () {
    if (!state.groups.length) {
      state.activeIndex = 0
      return
    }
    if (state.activeIndex < 0) state.activeIndex = 0
    if (state.activeIndex >= state.groups.length) state.activeIndex = state.groups.length - 1
  }
  const fnEmit = function () {
    const normalized = fnNormalizeKmzActionGroupsForPoint(state.groups, defaultPointIndex)
    state.groups = normalized
    fnEnsureActiveIndex()
    if (typeof onChange === 'function') onChange(_.cloneDeep(normalized))
  }
  const fnGetActiveGroupLive = function () {
    fnEnsureActiveIndex()
    return state.groups[state.activeIndex] || null
  }
  const fnMutateActiveGroupLive = function (mutator) {
    if (typeof mutator !== 'function') return false
    const group = fnGetActiveGroupLive()
    if (!group) return false
    mutator(group)
    return true
  }
  const fnListTriggerEntries = function (group) {
    const trigger = (group && group.actionTrigger && typeof group.actionTrigger === 'object')
      ? group.actionTrigger
      : {}
    return Object.keys(trigger)
      .filter((key) => String(key || '').trim() && key !== 'actionTriggerType')
      .map((key) => ({
        key: String(key || '').trim(),
        value: String(trigger[key] === undefined || trigger[key] === null ? '' : trigger[key])
      }))
  }
  const fnWriteTriggerEntries = function (group, entries, triggerTypeFallback) {
    if (!group || typeof group !== 'object') return
    const currentType = String(_.get(group, 'actionTrigger.actionTriggerType') || triggerTypeFallback || KMZ_DEFAULT_ACTION_TRIGGER_TYPE).trim() || KMZ_DEFAULT_ACTION_TRIGGER_TYPE
    const nextTrigger = { actionTriggerType: currentType }
    ;(Array.isArray(entries) ? entries : []).forEach((entry) => {
      const key = String(entry && entry.key || '').trim()
      if (!key) return
      nextTrigger[key] = String(entry && entry.value === undefined ? '' : entry.value)
    })
    group.actionTrigger = nextTrigger
  }
  const fnRenderPresetOptions = function () {
    const presetOptions = fnGetKmzActionGroupPresetOptions(defaultPointIndex)
    const $optionPrototype = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-option]')
    $presetSelect.empty()
    if (!$optionPrototype.length) {
      $presetSelect.prop('disabled', true)
      return presetOptions
    }
    const $placeholder = $optionPrototype.clone()
    $placeholder.attr('value', '').text(fnT('action.kmz.group_presets'))
    $presetSelect.append($placeholder)
    presetOptions.forEach((preset) => {
      const $option = $optionPrototype.clone()
      $option
        .attr('value', String(preset.value || ''))
        .text(String(preset.label || preset.value || ''))
      $presetSelect.append($option)
    })
    $presetSelect.prop('disabled', !presetOptions.length)
    $presetSelect.val('')
    return presetOptions
  }
  const fnRender = function () {
      fnEnsureActiveIndex()
    fnRenderPresetOptions()
      $title.text(fnT('action.kmz.groups_title'))
      $addLabel.text(fnT('action.kmz.group_add'))
      $nav.empty()
      $cardHost.empty()
      if (!state.groups.length) {
        $empty.text(fnT('action.none')).removeClass('d-none')
      } else {
        $empty.addClass('d-none').text('')
      }
      state.groups.forEach((group, idx) => {
        const $item = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-groupnavitem]')
        $item
          .attr('data-kmz-editor-groupindex', String(idx))
          .text('G' + String(idx + 1) + ' #' + String(group.actionGroupId || idx))
          .toggleClass('bg-primary', idx === state.activeIndex)
          .toggleClass('text-white', idx === state.activeIndex)
          .toggleClass('bg-light', idx !== state.activeIndex)
          .toggleClass('text-dark', idx !== state.activeIndex)
        $nav.append($item)
      })
      const activeGroup = fnGetActiveGroupLive()
      if (!activeGroup) {
        return
      }

      const $groupCard = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-groupcard]')
      $groupCard.attr('data-kmz-editor-groupindex', String(state.activeIndex))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupcard-title]').text(fnTF('action.kmz.group_label', { n: state.activeIndex + 1 }))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupdelete]').text(fnT('action.delete'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupmeta-startlabel]').text(fnT('action.kmz.start_index'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupmeta-endlabel]').text(fnT('action.kmz.end_index'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupmeta-modelabel]').text(fnT('action.kmz.group_mode'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggertype-label]').text(fnT('action.kmz.trigger_type'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggercustom-label]').text(fnT('action.kmz.trigger_custom'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggerfields-label]').text(fnT('action.kmz.trigger_fields'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggerfieldadd-label]').text(fnT('action.kmz.trigger_field_add'))
      fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggerfields-empty]').text(fnT('action.kmz.trigger_field_none'))

      const $up = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupmoveup]')
      const $down = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupmovedown]')
      $up.prop('disabled', state.activeIndex <= 0).toggleClass('opacity-50', state.activeIndex <= 0)
      $down.prop('disabled', state.activeIndex >= state.groups.length - 1).toggleClass('opacity-50', state.activeIndex >= state.groups.length - 1)

      const $startInput = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupstart-input]')
      const $endInput = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupend-input]')
      const $modeInput = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupmode-input]')
      $startInput.val(String(activeGroup.actionGroupStartIndex || defaultPointIndex))
      $endInput.val(String(activeGroup.actionGroupEndIndex || defaultPointIndex))
      $modeInput.val(String(activeGroup.actionGroupMode || KMZ_DEFAULT_ACTION_GROUP_MODE))

      const trigger = (activeGroup.actionTrigger && typeof activeGroup.actionTrigger === 'object')
        ? activeGroup.actionTrigger
        : {}
      const triggerType = String(trigger.actionTriggerType || KMZ_DEFAULT_ACTION_TRIGGER_TYPE).trim() || KMZ_DEFAULT_ACTION_TRIGGER_TYPE
      const isReachPointTrigger = triggerType.toLowerCase() === 'reachpoint'
      const reachPointAutoIndex = fnNormalizeKmzIntegerLikeText(defaultPointIndex, defaultPointIndex)
      $startInput
        .val(isReachPointTrigger ? reachPointAutoIndex : String(activeGroup.actionGroupStartIndex || defaultPointIndex))
        .prop('disabled', isReachPointTrigger)
        .attr('aria-disabled', isReachPointTrigger ? 'true' : 'false')
      $endInput
        .val(isReachPointTrigger ? reachPointAutoIndex : String(activeGroup.actionGroupEndIndex || defaultPointIndex))
        .prop('disabled', isReachPointTrigger)
        .attr('aria-disabled', isReachPointTrigger ? 'true' : 'false')
      if (isReachPointTrigger) {
        $startInput.attr('title', fnT('action.kmz.reachpoint_auto_index_tooltip'))
        $endInput.attr('title', fnT('action.kmz.reachpoint_auto_index_tooltip'))
        fnFindKmzEditorNode($groupCard, '[data-kmz-editor-reachpoint-note]')
          .text(fnT('action.kmz.reachpoint_auto_index_note'))
          .removeClass('d-none')
      } else {
        $startInput.removeAttr('title')
        $endInput.removeAttr('title')
        fnFindKmzEditorNode($groupCard, '[data-kmz-editor-reachpoint-note]').addClass('d-none').text('')
      }
      const triggerEntries = fnListTriggerEntries(activeGroup)
      const $triggerBadgeWrap = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggerbadgehost]')
      KMZ_ACTION_TRIGGER_TYPES.forEach((value) => {
        const $badge = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-triggerbadge]')
        $badge
          .attr('data-kmz-editor-triggertype', value)
          .text(value)
          .toggleClass('bg-primary', value === triggerType)
          .toggleClass('text-white', value === triggerType)
          .toggleClass('bg-light', value !== triggerType)
          .toggleClass('text-dark', value !== triggerType)
        $triggerBadgeWrap.append($badge)
      })
      const $triggerCustomInput = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggercustom-input]')
      $triggerCustomInput.val(triggerType)
      const $triggerFieldsEmpty = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggerfields-empty]')
      $triggerFieldsEmpty.toggleClass('d-none', !!triggerEntries.length)
      const $triggerFieldList = fnFindKmzEditorNode($groupCard, '[data-kmz-editor-triggerfieldlist]')
      triggerEntries.forEach((entry, triggerIdx) => {
        const $line = fnCloneKmzPointEditorNode($bank, '[data-kmz-editor-triggerfieldrow]')
        $line
          .attr('data-kmz-editor-groupindex', String(state.activeIndex))
          .attr('data-kmz-editor-triggerfieldindex', String(triggerIdx))
        fnFindKmzEditorNode($line, '[data-kmz-editor-triggerfieldkey]').val(String(entry.key || ''))
        fnFindKmzEditorNode($line, '[data-kmz-editor-triggerfieldvalue]').val(String(entry.value || ''))
        fnFindKmzEditorNode($line, '[data-kmz-editor-triggerfielddelete]').text(fnT('action.delete'))
        $triggerFieldList.append($line)
      })

      renderKmzActionsEditor(fnFindKmzEditorNode($groupCard, '[data-kmz-editor-groupactions]'), activeGroup.actions || [], function (nextActions) {
        fnMutateActiveGroupLive((group) => {
          group.actions = Array.isArray(nextActions) ? nextActions : []
        })
        fnEmit()
      }, {
        fileSuffixMode: opts.fileSuffixMode,
        pointNameResolver: opts.pointNameResolver
      })
      $cardHost.append($groupCard)

    }

  $root
    .off('.portalKmzGroups')
    .on('change.portalKmzGroups', '[data-kmz-editor-presetselect]', function () {
      const presetValue = String($(this).val() || '').trim()
      $(this).val('')
      if (!presetValue) return
      const presetOptions = fnGetKmzActionGroupPresetOptions(defaultPointIndex)
      const selectedPreset = presetOptions.find((preset) => String(preset && preset.value || '').trim() === presetValue) || null
      if (!selectedPreset) return
      state.groups = _.cloneDeep(Array.isArray(selectedPreset.groups) ? selectedPreset.groups : [])
      state.activeIndex = 0
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-groupadd]', function () {
      const nextGroup = fnBuildDefaultGroup(state.groups.length)
      nextGroup.actionGroupStartIndex = String(defaultPointIndex)
      nextGroup.actionGroupEndIndex = String(defaultPointIndex)
      state.groups.push(nextGroup)
      state.activeIndex = state.groups.length - 1
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-groupnavitem]', function () {
      state.activeIndex = fnParseKmzEditorIndexAttr($(this).attr('data-kmz-editor-groupindex'))
      fnRender()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-groupmoveup]', function () {
      if (state.activeIndex <= 0) return
      const temp = state.groups[state.activeIndex - 1]
      state.groups[state.activeIndex - 1] = state.groups[state.activeIndex]
      state.groups[state.activeIndex] = temp
      state.activeIndex -= 1
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-groupmovedown]', function () {
      if (state.activeIndex < 0 || state.activeIndex >= state.groups.length - 1) return
      const temp = state.groups[state.activeIndex + 1]
      state.groups[state.activeIndex + 1] = state.groups[state.activeIndex]
      state.groups[state.activeIndex] = temp
      state.activeIndex += 1
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-groupdelete]', function () {
      if (!state.groups.length) return
      state.groups.splice(state.activeIndex, 1)
      if (state.activeIndex >= state.groups.length) state.activeIndex = state.groups.length - 1
      fnRender()
      fnEmit()
    })
    .on('input.portalKmzGroups', '[data-kmz-editor-groupstart-input]', function () {
      const group = fnGetActiveGroupLive()
      if (!group) return
      const liveTriggerType = String(_.get(group, 'actionTrigger.actionTriggerType') || KMZ_DEFAULT_ACTION_TRIGGER_TYPE).trim().toLowerCase()
      if (liveTriggerType === 'reachpoint') return
      group.actionGroupStartIndex = fnNormalizeKmzIntegerLikeText($(this).val(), defaultPointIndex)
      fnEmit()
    })
    .on('input.portalKmzGroups', '[data-kmz-editor-groupend-input]', function () {
      const group = fnGetActiveGroupLive()
      if (!group) return
      const liveTriggerType = String(_.get(group, 'actionTrigger.actionTriggerType') || KMZ_DEFAULT_ACTION_TRIGGER_TYPE).trim().toLowerCase()
      if (liveTriggerType === 'reachpoint') return
      group.actionGroupEndIndex = fnNormalizeKmzIntegerLikeText($(this).val(), defaultPointIndex)
      fnEmit()
    })
    .on('input.portalKmzGroups', '[data-kmz-editor-groupmode-input]', function () {
      const group = fnGetActiveGroupLive()
      if (!group) return
      group.actionGroupMode = String($(this).val() || '').trim() || KMZ_DEFAULT_ACTION_GROUP_MODE
      fnEmit()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-triggerbadge]', function () {
      const nextType = String($(this).attr('data-kmz-editor-triggertype') || '').trim()
      if (!nextType) return
      fnMutateActiveGroupLive((group) => {
        group.actionTrigger = group.actionTrigger || {}
        group.actionTrigger.actionTriggerType = nextType
      })
      fnRender()
      fnEmit()
    })
    .on('input.portalKmzGroups', '[data-kmz-editor-triggercustom-input]', function () {
      const nextType = String($(this).val() || '').trim() || KMZ_DEFAULT_ACTION_TRIGGER_TYPE
      fnMutateActiveGroupLive((group) => {
        group.actionTrigger = group.actionTrigger || {}
        group.actionTrigger.actionTriggerType = nextType
      })
      fnRender()
      fnEmit()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-triggerfieldadd]', function () {
      const group = fnGetActiveGroupLive()
      if (!group) return
      const entries = fnListTriggerEntries(group)
      const used = entries.map((entry) => String(entry && entry.key || '').trim()).filter((key) => !!key)
      let nextKey = used.includes('actionTriggerParam') ? '' : 'actionTriggerParam'
      if (!nextKey) {
        let i = 1
        nextKey = 'triggerParam' + i
        while (used.includes(nextKey)) {
          i += 1
          nextKey = 'triggerParam' + i
        }
      }
      entries.push({ key: nextKey, value: '' })
      fnWriteTriggerEntries(group, entries)
      fnRender()
      fnEmit()
    })
    .on('input.portalKmzGroups', '[data-kmz-editor-triggerfieldkey]', function () {
      const group = fnGetActiveGroupLive()
      if (!group) return
      const $line = $(this).closest('[data-kmz-editor-triggerfieldrow]')
      const triggerIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-triggerfieldindex'))
      const entries = fnListTriggerEntries(group)
      if (!entries[triggerIndex]) return
      entries[triggerIndex].key = String($(this).val() || '').trim()
      fnWriteTriggerEntries(group, entries)
      fnRender()
      fnEmit()
    })
    .on('input.portalKmzGroups', '[data-kmz-editor-triggerfieldvalue]', function () {
      const group = fnGetActiveGroupLive()
      if (!group) return
      const $line = $(this).closest('[data-kmz-editor-triggerfieldrow]')
      const triggerIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-triggerfieldindex'))
      const entries = fnListTriggerEntries(group)
      if (!entries[triggerIndex]) return
      entries[triggerIndex].value = String($(this).val() || '')
      fnWriteTriggerEntries(group, entries)
      fnEmit()
    })
    .on('click.portalKmzGroups', '[data-kmz-editor-triggerfielddelete]', function () {
      const group = fnGetActiveGroupLive()
      if (!group) return
      const $line = $(this).closest('[data-kmz-editor-triggerfieldrow]')
      const triggerIndex = fnParseKmzEditorIndexAttr($line.attr('data-kmz-editor-triggerfieldindex'))
      const entries = fnListTriggerEntries(group)
      if (triggerIndex < 0 || triggerIndex >= entries.length) return
      entries.splice(triggerIndex, 1)
      fnWriteTriggerEntries(group, entries)
      fnRender()
      fnEmit()
    })

  $container.empty().append($root)
  fnRender()
  if (opts.emitInitial) fnEmit()
}

export {
  renderKmzActionsEditor,
  renderKmzActionGroupsEditor,
  fnNormalizeKmzActionGroupsForPoint as normalizeKmzActionGroupsForPoint,
  fnGetKmzActionPreset as getKmzActionPreset,
  fnGetKmzAllowedParamKeys as getKmzAllowedParamKeys,
  fnGetKmzParamMeta as getKmzParamMeta,
  fnValidateKmzParamValue as validateKmzParamValue,
  fnBuildKmzParamInfoText as buildKmzParamInfoText,
  fnNormalizeKmzActionParamsForActuator as normalizeKmzActionParamsForActuator
}
